// drawOla multi-file storage — types.
//
// Fork-only. No upstream counterpart, so it never conflicts on merge.

import type { OrderedExcalidrawElement } from "@excalidraw/element/types";
import type { AppState, BinaryFiles } from "@excalidraw/excalidraw/types";

/**
 * Sync errors the user has to act on. Auth is not one of them: it is read
 * from the provider's live auth state. Network errors retry on their own.
 */
export type CloudSyncError = "conflict" | "fileMissing" | "tooLarge";

/** Cloud-provider binding for a local doc. Set after a successful Save /
 *  Save As / Open from cloud, and kept current by the cloud sync engine. */
export type CloudBinding = {
  providerId: "googleDrive" | "dropbox" | "oneDrive" | "pCloud" | "nextcloud";
  providerFileId: string;
  /** Opaque etag / rev as returned by the provider. */
  lastSyncedVersion: string;
  /** Epoch ms of the successful sync. */
  lastSyncedAt: number;
  /**
   * `DocMeta.sceneKey` of the scene last pushed or pulled. Differs from the
   * current one when the local copy has edits the cloud has not seen.
   */
  lastSyncedSceneKey?: string;
  /** Present while the user has to act before sync can continue. */
  syncError?: CloudSyncError;
};

/**
 * How a draw came to belong to a live collaboration room.
 *
 * `"created"` means the draw exists ONLY because this browser joined that room
 * by link: it was made by `openCollabSessionDoc` moments before the marker was
 * written, and it holds nothing but the room's scene. It is therefore the one
 * and only kind of draw a discard-policy session is allowed to remove.
 *
 * `"shared"` means the user opened the room from a draw they already had. That
 * draw is their own work and is never removed, whatever the policy says.
 */
export type DocCollabOrigin = "created" | "shared";

/**
 * The live collaboration room a draw belongs to, while a session is running.
 *
 * Persisted (rather than kept in memory) for two reasons: a page reload is the
 * documented way to rejoin a room, and it has to find the same draw again
 * instead of making a second one; and the delete path can then prove what it is
 * about to remove from the stored record rather than from a variable.
 *
 * Cleared at the end of every session, so a stale marker can never authorise a
 * later delete.
 */
export type DocCollabRoom = {
  roomId: string;
  origin: DocCollabOrigin;
};

export type DocMeta = {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  /**
   * Approximate total bytes: scene JSON length + sum of binary dataURL lengths.
   * Excludes `thumbnail` — see below.
   */
  sizeBytes: number;
  /**
   * Identity of the content the cloud copy would hold: element versions,
   * element count, file ids and name. `appState` is left out on purpose so
   * zoom, scroll and selection never look like an edit to the cloud sync.
   * Maintained by `saveDoc`, `createDoc` and `renameDoc`; kept on the meta so
   * the sync engine's boot scan does not have to load every scene.
   */
  sceneKey?: string;
  /**
   * Small light-background scene preview as a data URL (WebP, PNG fallback),
   * ~320px on the long edge. Absent for an empty scene, and for docs the
   * Studio backfill has not reached yet — the grid falls back to a placeholder.
   *
   * NOT counted in `sizeBytes`: `estimateDocBytes()` is what the pre-flight
   * `canWrite()` check recomputes from the live scene, so folding the preview
   * in would desync ledger and check and could push a doc over
   * `MAX_BYTES_PER_DOC` for an invisible reason. See `./thumbnails.ts`.
   */
  thumbnail?: string;
  /** Set when this doc has been synced to a cloud provider. */
  cloud?: CloudBinding;
  /**
   * Set while this draw is the one a live collaboration session writes into.
   * Absent on every draw that was never in a room, which is what keeps the
   * discard path from ever reaching a document the user made themselves.
   */
  collabRoom?: DocCollabRoom;
  /**
   * Shareable links this doc has produced. `expiresAt` is stored rather than
   * derived from `createdAt` so a later change to the expiry policy does not
   * move the date shown for links that already exist.
   */
  shares?: DocShare[];
};

export type DocShare = {
  /** Storage id of the share, 32 lowercase hex. Also the object key. */
  id: string;
  /** AES-GCM key that decrypts the share. Lives in the link fragment too. */
  key: string;
  /** Random 32 byte hex secret that authorises revoking this share. */
  revokeSecret: string;
  createdAt: number;
  expiresAt: number;
};

export type Doc = DocMeta & {
  elements: readonly OrderedExcalidrawElement[];
  appState: Partial<AppState>;
  files: BinaryFiles;
};

export type CanWriteReason =
  | "doc-count"
  | "doc-size"
  | "policy-total"
  | "quota";

export type CanWriteResult =
  | { ok: true }
  | {
      ok: false;
      reason: CanWriteReason;
      /** Numbers the UI uses to format a localized message. */
      meta: {
        size?: number;
        quota?: number;
        usage?: number;
        max?: number;
      };
    };

export type StorageSnapshot = {
  /** Bytes drawOla has stored (scene + binaries sum across docs). */
  drawolaUsage: number;
  /** origin-wide usage from navigator.storage.estimate() */
  originUsage: number;
  /** browser quota for this origin */
  quota: number;
  /** min(policyTotal, quota × 0.9) */
  effectiveLimit: number;
  /** how many docs exist */
  docCount: number;
  /** is navigator.storage.persisted() */
  persisted: boolean;
};

export const LIMITS = {
  MAX_DOCUMENTS: 100,
  MAX_BYTES_PER_DOC: 100 * 1024 * 1024, // 100 MB, scene + files
  POLICY_TOTAL_BYTES: 100 * 100 * 1024 * 1024, // 10 GB
  QUOTA_HEADROOM_RATIO: 0.9,
  WARN_AT: 0.8,
  BLOCK_NEW_AT: 0.95,
  LOW_QUOTA_THRESHOLD: 500 * 1024 * 1024, // 500 MB
} as const;
